import { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import BaseLayout from '@components/1-atoms/Layouts/BaseLayout';
import { ConversationsProvider } from '@contexts/ConversationsContext';
import { LoadingSpinner } from '@components/LoadingSpinner';
import { useNotificationStream } from '@hooks/notifications/useNotificationStream';
import { FeatureGate, FeatureGated } from '@featureFlags/index';
import HomePage from '../pages/HomePage';
import InstructionsPage from '../pages/InstructionsPage';
import ProcessesPage from '../pages/Processes';
import ProcessEditorPage from '../pages/Processes/ProcessEditorPage';
import ProcessEditorSidebar from '../pages/Processes/editor/ProcessEditorSidebar';
import ProcessesThemeBoundary from '../pages/Processes/ProcessesThemeBoundary';
import SettingsPage from '../pages/SettingsPage';
import ConversationPage from '../pages/ConversationPage';
import BillingSuccess from '../pages/BillingSuccess/BillingSuccess';
import InviteAccept from '../pages/InviteAccept/InviteAccept';
import SkillsPage from '../pages/Skills/SkillsPage';
import AppsGalleryPage from '../pages/Apps/AppsGalleryPage';
import AppRunPage from '../pages/Apps/AppRunPage';
import AppBuilderPage from '../pages/Apps/builder/AppBuilderPage';
import { APP_BUILDER_ROUTE } from '../pages/Apps/builder/userAppStatus';
import AppLinkLanding from '../pages/Apps/public/AppLinkLanding';
import AppsGated from './AppsGated';

const AuthenticatedRoutes = () => {
  useNotificationStream();

  return (
    <ConversationsProvider>
      <BaseLayout>
        {/*
          Sidebar slot — `ProcessEditorSidebar` swaps in the NodeLibrary
          while the user is inside the editor route, falling back to the
          global NavigationBar everywhere else.
        */}
        <ProcessEditorSidebar />
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/conversation/:conversationId" element={<ConversationPage />} />
            <Route
              path="/processes"
              element={
                <FeatureGated
                  gate={FeatureGate.OS_PROCESSES_PAGE}
                  fallback={<Navigate to="/" replace />}
                  pending={<LoadingSpinner />}
                >
                  <ProcessesThemeBoundary>
                    <ProcessesPage />
                  </ProcessesThemeBoundary>
                </FeatureGated>
              }
            />
            <Route
              path="/processes/:processId"
              element={
                <FeatureGated
                  gate={FeatureGate.OS_PROCESSES_PAGE}
                  fallback={<Navigate to="/" replace />}
                  pending={<LoadingSpinner />}
                >
                  <ProcessesThemeBoundary>
                    <ProcessEditorPage />
                  </ProcessesThemeBoundary>
                </FeatureGated>
              }
            />
            <Route
              path="/processes/:processId/runs/:runId"
              element={
                <FeatureGated
                  gate={FeatureGate.OS_PROCESSES_PAGE}
                  fallback={<Navigate to="/" replace />}
                  pending={<LoadingSpinner />}
                >
                  <ProcessesThemeBoundary>
                    <ProcessEditorPage />
                  </ProcessesThemeBoundary>
                </FeatureGated>
              }
            />
            <Route
              path="/apps"
              element={
                <AppsGated>
                  <AppsGalleryPage />
                </AppsGated>
              }
            />
            {/* Same screen either way: without a run id it is the composer for
                a run that does not exist yet. */}
            <Route
              path="/apps/:appKey"
              element={
                <AppsGated>
                  <AppRunPage />
                </AppsGated>
              }
            />
            <Route
              path="/apps/shared/:token"
              element={
                <AppsGated>
                  <AppLinkLanding />
                </AppsGated>
              }
            />
            <Route
              path={APP_BUILDER_ROUTE}
              element={
                <AppsGated>
                  <FeatureGated
                    gate={FeatureGate.OS_APP_BUILDER}
                    fallback={<Navigate to="/apps" replace />}
                    pending={<LoadingSpinner />}
                  >
                    <AppBuilderPage />
                  </FeatureGated>
                </AppsGated>
              }
            />
            <Route
              path="/apps/:appKey/runs/:runId"
              element={
                <AppsGated>
                  <AppRunPage />
                </AppsGated>
              }
            />
            <Route path="/instructions" element={<InstructionsPage />} />
            <Route path="/knowledge-base" element={<Navigate to="/instructions" replace />} />
            <Route path="/skills" element={<SkillsPage />} />
            <Route path="/settings/*" element={<SettingsPage />} />
            <Route path="/billing" element={<Navigate to="/settings/billing" replace />} />
            <Route path="/billing/success" element={<BillingSuccess />} />
            <Route path="/organization" element={<Navigate to="/settings/organization" replace />} />
            <Route path="/local-runners" element={<Navigate to="/settings/local-runners" replace />} />
            <Route path="/security" element={<Navigate to="/settings/security" replace />} />
            <Route path="/org/invite/token/:token" element={<InviteAccept />} />
            <Route path="/org/invite" element={<InviteAccept />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BaseLayout>
    </ConversationsProvider>
  );
};

export default AuthenticatedRoutes;
